import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  CubeIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShieldExclamationIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  FolderIcon,
  RectangleStackIcon,
  BriefcaseIcon,
  ListBulletIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import { useProducts } from '../hooks/useProducts';
import { useWarehouses } from '../hooks/useWarehouses';
import { useInventory } from '../hooks/useInventory';
import { useCategories } from '../hooks/useCategories';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { useStorage } from '../hooks/useStorage';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const DashboardModern = () => {
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { warehouses } = useWarehouses();
  const { inventory } = useInventory();
  const { categories } = useCategories();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { users } = useUsers();
  const { getCostOptimizationData } = useStorage();
  const [storageData, setStorageData] = React.useState<any>(null);

  // Load storage data
  React.useEffect(() => {
    const loadStorageData = async () => {
      try {
        const data = await getCostOptimizationData(user?.organizationId);
        setStorageData(data);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    if (user?.organizationId) {
      loadStorageData();
    }
  }, [user?.organizationId, getCostOptimizationData]);

  // Greeting logic
  const currentHour = new Date().getHours();
  let greeting = t('dashboard.welcome.afternoon');
  
  if (currentHour < 12) {
    greeting = t('dashboard.welcome.morning');
  } else if (currentHour < 18) {
    greeting = t('dashboard.welcome.afternoon');
  } else {
    greeting = t('dashboard.welcome.evening');
  }

  // Quick navigation tiles configuration
  const allQuickNavTiles = [
    {
      name: t('nav.customers'),
      href: '/customers',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      count: customers.length,
      module: 'customers'
    },
    {
      name: t('nav.suppliers'),
      href: '/suppliers',
      icon: BuildingStorefrontIcon,
      color: 'from-emerald-500 to-emerald-600',
      count: null,
      module: 'suppliers'
    },
    {
      name: t('nav.products'),
      href: '/products',
      icon: CubeIcon,
      color: 'from-orange-500 to-orange-600',
      count: products.length,
      module: 'products'
    },
    {
      name: t('nav.categories'),
      href: '/categories',
      icon: TagIcon,
      color: 'from-purple-500 to-purple-600',
      count: categories.length,
      module: 'products'
    },
    {
      name: t('nav.warehouse'),
      href: '/warehouse',
      icon: BuildingStorefrontIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: warehouses.filter(w => w.isActive).length,
      module: 'inventory'
    },
    {
      name: t('nav.inventory'),
      href: '/inventory',
      icon: ClipboardDocumentListIcon,
      color: 'from-teal-500 to-teal-600',
      count: inventory.length,
      module: 'inventory'
    },
    {
      name: t('nav.sales'),
      href: '/sales',
      icon: ShoppingCartIcon,
      color: 'from-green-500 to-green-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.quotations'),
      href: '/quotations',
      icon: DocumentTextIcon,
      color: 'from-rose-500 to-rose-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.orders'),
      href: '/orders',
      icon: ShoppingCartIcon,
      color: 'from-blue-500 to-blue-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.delivery_notes'),
      href: '/delivery-notes',
      icon: TruckIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.warranty'),
      href: '/warranty',
      icon: ShieldExclamationIcon,
      color: 'from-yellow-500 to-yellow-600',
      count: null,
      module: 'products'
    },
    {
      name: t('nav.billing'),
      href: '/billing',
      icon: ReceiptPercentIcon,
      color: 'from-red-500 to-red-600',
      count: null,
      module: 'billing'
    },
    {
      name: t('nav.deals'),
      href: '/deals',
      icon: CurrencyDollarIcon,
      color: 'from-pink-500 to-pink-600',
      count: null,
      module: 'deals'
    },
    {
      name: t('nav.contracts'),
      href: '/contracts',
      icon: DocumentTextIcon,
      color: 'from-violet-500 to-violet-600',
      count: null,
      module: 'contracts'
    },
    {
      name: t('nav.worksheets'),
      href: '/worksheets',
      icon: ClipboardDocumentListIcon,
      color: 'from-slate-500 to-slate-600',
      count: null,
      module: 'worksheets'
    },
    {
      name: t('nav.tasks'),
      href: '/tasks',
      icon: ClipboardDocumentListIcon,
      color: 'from-cyan-500 to-cyan-600',
      count: null,
      module: 'tasks'
    },
    {
      name: t('nav.templates'),
      href: '/templates',
      icon: DocumentTextIcon,
      color: 'from-amber-500 to-amber-600',
      count: null,
      module: 'templates'
    },
    {
      name: t('nav.ai_quotations'),
      href: '/ai-quotations',
      icon: SparklesIcon,
      color: 'from-purple-500 to-purple-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.documentStorage'),
      href: '/document-storage',
      icon: FolderIcon,
      color: 'from-blue-500 to-blue-600',
      count: null,
      module: 'customers'
    },
    {
      name: t('nav.formBuilder'),
      href: '/form-builder',
      icon: RectangleStackIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: null,
      module: 'forms'
    },
    {
      name: 'Flow Builder',
      href: '/flow-builder',
      icon: Cog6ToothIcon,
      color: 'from-violet-500 to-violet-600',
      count: null,
      module: 'forms'
    },
    {
      name: t('nav.myForms'),
      href: '/my-forms',
      icon: ListBulletIcon,
      color: 'from-teal-500 to-teal-600',
      count: null,
      module: 'forms'
    },
    {
      name: t('nav.projects'),
      href: '/projects',
      icon: BriefcaseIcon,
      color: 'from-emerald-500 to-emerald-600',
      count: null,
      module: 'projects'
    },
    {
      name: t('nav.reports'),
      href: '/reports',
      icon: ChartBarIcon,
      color: 'from-red-500 to-red-600',
      count: null,
      module: 'reports'
    },
    {
      name: t('nav.settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-gray-600',
      count: null,
      module: 'settings'
    }
  ];

  if (user?.role === 'super_admin') {
    allQuickNavTiles.push({
      name: t('nav.system_admin'),
      href: '/system-admin',
      icon: ShieldCheckIcon,
      color: 'from-cyan-500 to-cyan-600',
      count: null,
      module: 'admin'
    });
  }

  const quickNavTiles = allQuickNavTiles.filter(tile => {
    return hasAccess(tile.module as keyof import('../types/user').UserModulePermissions);
  });

  // Inventory stats
  const inventoryStats = useMemo(() => {
    const totalItems = inventory.length;
    const lowStockItems = inventory.filter(item => 
      item.minQuantity && item.quantity < item.minQuantity
    ).length;
    const outOfStockItems = inventory.filter(item => item.quantity === 0).length;
    const totalValue = inventory.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + (item.quantity * (product?.purchasePrice || 0));
    }, 0);

    return {
      totalItems,
      lowStockItems,
      outOfStockItems,
      totalValue
    };
  }, [inventory, products]);

  // Recent customers
  const recentCustomers = useMemo(() => {
    return [...customers]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [customers]);

  // Recent products
  const recentProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [products]);

  // Low stock products
  const lowStockProducts = useMemo(() => {
    return inventory
      .filter(item => item.minQuantity && item.quantity < item.minQuantity)
      .sort((a, b) => {
        const aRatio = a.quantity / (a.minQuantity || 1);
        const bRatio = b.quantity / (b.minQuantity || 1);
        return aRatio - bRatio;
      })
      .slice(0, 5);
  }, [inventory]);

  // Real database growth data - last 6 months
  const databaseGrowthData = useMemo(() => {
    const now = new Date();
    const monthsData = [];
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthName = monthDate.toLocaleDateString('hu-HU', { month: 'short' });
      
      // Count items created in this month
      const customersInMonth = customers.filter(c => {
        if (!c.createdAt) return false;
        return c.createdAt >= monthDate && c.createdAt <= monthEnd;
      }).length;
      
      const productsInMonth = products.filter(p => {
        if (!p.createdAt) return false;
        return p.createdAt >= monthDate && p.createdAt <= monthEnd;
      }).length;
      
      const inventoryInMonth = inventory.filter(inv => {
        // Inventory items may not have createdAt, skip them
        return false; // TODO: Add createdAt to Inventory type if needed
      }).length;
      
      monthsData.push({
        name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        customers: customersInMonth,
        products: productsInMonth,
        inventory: inventoryInMonth,
        total: customersInMonth + productsInMonth + inventoryInMonth
      });
    }
    
    return monthsData;
  }, [customers, products, inventory]);

  const pieData = [
    { name: 'Ügyfelek', value: customers.length, color: '#3b82f6' },
    { name: 'Termékek', value: products.length, color: '#f97316' },
    { name: 'Készlet', value: inventory.length, color: '#14b8a6' },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 100
      }
    }
  };

  // No organization check
  if (!user?.organizationId && user?.role !== 'super_admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 text-blue-500 mb-8">
            <UserGroupIcon className="h-full w-full" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Üdvözöljük, {user?.displayName}!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            A fiókja beállítás alatt van. Kérjük, vegye fel a kapcsolatot a rendszergazdával a hozzáférés aktiválásához.
          </p>
        </div>
      </div>
    );
  }

  // Real system info from hooks
  const totalStorageGB = storageData ? storageData.totalSize / (1024 * 1024 * 1024) : 0;
  const activeUsers = users.filter(u => {
    // Aktív felhasználók: akik az elmúlt 30 napban bejelentkeztek
    if (!u.lastLoginAt) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return u.lastLoginAt >= thirtyDaysAgo;
  }).length;
  
  const systemInfo = {
    storage: {
      used: totalStorageGB,
      total: 100, // Ez lehet egy package limit a jövőben
      unit: 'GB'
    },
    users: {
      active: activeUsers,
      total: users.length
    },
    database: {
      records: customers.length + products.length + inventory.length + users.length,
      size: totalStorageGB
    },
    uptime: '99.9%' // Ez egy külső monitoring service-ből jöhetne
  };

  const storagePercentage = (systemInfo.storage.used / systemInfo.storage.total) * 100;

  return (
    <div className="space-y-8 pb-20 sm:pb-0">
        
        {/* System Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20 shadow-lg"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Storage */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 dark:text-gray-400">Tárhely</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.storage.used.toFixed(2)} / {systemInfo.storage.total} {systemInfo.storage.unit}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Active Users */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                <UserGroupIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Aktív felhasználók</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.users.active} / {systemInfo.users.total}
                </div>
              </div>
            </div>

            {/* Database Records */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Adatbázis rekordok</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.database.records.toLocaleString()}
                  {storageData && storageData.totalFiles > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      ({storageData.totalFiles} fájl)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* System Uptime */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Rendszer elérhetőség</div>
                <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                  {systemInfo.uptime}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Hero Header with Glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 p-8 md:p-12 shadow-2xl"
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>
          
          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center gap-4 mb-4"
            >
              <div className="p-4 bg-white/20 backdrop-blur-xl rounded-2xl">
                <ChartBarIcon className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                  {t('dashboard.title')}
                </h1>
                <p className="text-lg md:text-xl text-white/90 flex items-center gap-2">
                  {greeting}, <span className="font-semibold">{user?.displayName || t('dashboard.welcome.user')}</span>! 👋
                </p>
              </div>
            </motion.div>
            
            {/* Quick stats in hero */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
            >
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Ügyfelek</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{customers.length}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Termékek</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{products.length}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Készlet</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{inventory.length}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Raktárak</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{warehouses.filter(w => w.isActive).length}</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Quick Navigation - Glassmorphism Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="relative overflow-hidden backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-3xl p-6 md:p-8 border border-white/20 dark:border-gray-700/20 shadow-xl"
        >
          {/* Subtle background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5 dark:from-purple-500/10 dark:to-blue-500/10"></div>
          
          <div className="relative z-10">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-3">
                <SparklesIcon className="w-7 h-7 text-purple-600" />
                Gyors navigáció
              </h2>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 md:gap-4">
            {quickNavTiles.map((tile, index) => (
              <motion.div
                key={tile.name}
                variants={itemVariants}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={tile.href}
                  className="group relative overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20 shadow-lg hover:shadow-2xl transition-all duration-300 block h-full"
                >
                  <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                    {/* Icon with gradient */}
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${tile.color} text-white shadow-lg transition-shadow duration-200`}>
                      <tile.icon className="w-6 h-6" />
                    </div>
                    
                    {/* Title */}
                    <div className="space-y-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-xs md:text-sm leading-tight">
                        {tile.name}
                      </h3>
                      {tile.count !== null && (
                        <span className="inline-block px-2.5 py-1 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
                          {tile.count}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
            </div>
          </div>
        </motion.div>

        {/* Inventory Stats - Glassmorphism Cards */}
        {inventoryStats.totalItems > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-3">
              <ChartBarIcon className="w-7 h-7 text-indigo-600" />
              {t('dashboard.inventoryOverview')}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/20 rounded-2xl p-6 border border-blue-200/50 dark:border-blue-700/50 shadow-xl"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <ClipboardDocumentListIcon className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                    <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">{inventoryStats.totalItems}</div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalItems')}</div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 dark:from-orange-500/20 dark:to-amber-500/20 rounded-2xl p-6 border border-orange-200/50 dark:border-orange-700/50 shadow-xl"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <ExclamationTriangleIcon className="w-10 h-10 text-orange-600 dark:text-orange-400" />
                    <ArrowTrendingDownIcon className="w-6 h-6 text-orange-500" />
                  </div>
                  <div className="text-4xl font-bold text-orange-600 dark:text-orange-400 mb-2">{inventoryStats.lowStockItems}</div>
                  <div className="text-sm font-medium text-orange-600 dark:text-orange-400">{t('dashboard.lowStock')}</div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-red-500/10 to-rose-500/10 dark:from-red-500/20 dark:to-rose-500/20 rounded-2xl p-6 border border-red-200/50 dark:border-red-700/50 shadow-xl"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <ShieldExclamationIcon className="w-10 h-10 text-red-600 dark:text-red-400" />
                    <FireIcon className="w-6 h-6 text-red-500" />
                  </div>
                  <div className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2">{inventoryStats.outOfStockItems}</div>
                  <div className="text-sm font-medium text-red-600 dark:text-red-400">{t('dashboard.outOfStock')}</div>
                </div>
              </motion.div>
              
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 dark:from-green-500/20 dark:to-emerald-500/20 rounded-2xl p-6 border border-green-200/50 dark:border-green-700/50 shadow-xl"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <CurrencyDollarIcon className="w-10 h-10 text-green-600 dark:text-green-400" />
                    <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    {new Intl.NumberFormat('hu-HU', {
                      style: 'currency',
                      currency: 'HUF',
                      maximumFractionDigits: 0
                    }).format(inventoryStats.totalValue)}
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400">{t('dashboard.totalValue')}</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Area Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
              <ArrowTrendingUpIcon className="w-6 h-6 text-blue-600" />
              Adatbázis növekedés (utolsó 6 hónap)
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={databaseGrowthData}>
                <defs>
                  <linearGradient id="colorCustomers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorProducts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} 
                />
                <Area type="monotone" dataKey="customers" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCustomers)" name="Ügyfelek" />
                <Area type="monotone" dataKey="products" stroke="#f97316" fillOpacity={1} fill="url(#colorProducts)" name="Termékek" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Pie Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
              <ChartBarIcon className="w-6 h-6 text-indigo-600" />
              Adatok megoszlása
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Detailed Information - 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Customers */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="relative backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent flex items-center gap-2">
                  <UserGroupIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  {t('dashboard.recentCustomers')}
                </h2>
                <Link 
                  to="/customers" 
                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Összes →
                </Link>
              </div>
              
              <div className="space-y-3">
                {recentCustomers.length > 0 ? (
                  recentCustomers.map((customer, index) => (
                    <motion.div
                      key={customer.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      className="group/item relative overflow-hidden backdrop-blur-sm bg-white/80 dark:bg-gray-700/80 rounded-xl p-4 border border-white/40 dark:border-gray-600/40 shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-indigo-500/0 group-hover/item:from-blue-500/10 group-hover/item:to-indigo-500/10 transition-all duration-300"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-colors duration-300">
                            {customer.type === 'organization' 
                              ? customer.companyName 
                              : `${customer.lastName} ${customer.firstName}`}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                            📧 {customer.email}
                          </p>
                        </div>
                        {customer.createdAt && (
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-3 py-1.5 rounded-full group-hover/item:bg-blue-100 dark:group-hover/item:bg-blue-900/40 group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-all duration-300">
                            {format(customer.createdAt, 'MM/dd')}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    <UserGroupIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('dashboard.noCustomers')}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Recent Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="relative backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent flex items-center gap-2">
                  <CubeIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                  {t('dashboard.recentProducts')}
                </h2>
                <Link 
                  to="/products" 
                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full hover:from-green-600 hover:to-emerald-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Összes →
                </Link>
              </div>
              
              <div className="space-y-3">
                {recentProducts.length > 0 ? (
                  recentProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + index * 0.05 }}
                      whileHover={{ scale: 1.02 }}
                      className="group/item relative overflow-hidden backdrop-blur-sm bg-white/80 dark:bg-gray-700/80 rounded-xl p-4 border border-white/40 dark:border-gray-600/40 shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 to-emerald-500/0 group-hover/item:from-green-500/10 group-hover/item:to-emerald-500/10 transition-all duration-300"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover/item:text-green-600 dark:group-hover/item:text-green-400 transition-colors duration-300">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            📦 {product.code}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover/item:text-green-600 dark:group-hover/item:text-green-400 transition-colors duration-300">
                            {new Intl.NumberFormat('hu-HU', {
                              style: 'currency',
                              currency: 'HUF',
                              maximumFractionDigits: 0
                            }).format(product.netPrice || 0)}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    <CubeIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('dashboard.noProducts')}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Low Stock Products */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="relative backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 dark:from-orange-400 dark:to-red-400 bg-clip-text text-transparent flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  {t('dashboard.lowStockProducts')}
                </h2>
                <Link 
                  to="/inventory" 
                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full hover:from-orange-600 hover:to-red-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Összes →
                </Link>
              </div>
              
              <div className="space-y-3">
                {lowStockProducts.length > 0 ? (
                  lowStockProducts.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + index * 0.05 }}
                      whileHover={{ scale: 1.02 }}
                      className="group/item relative overflow-hidden backdrop-blur-sm bg-gradient-to-r from-red-50/80 to-orange-50/60 dark:from-red-900/30 dark:to-orange-900/20 rounded-xl p-4 border border-red-200/60 dark:border-red-700/40 shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover/item:text-red-600 dark:group-hover/item:text-red-400 transition-colors duration-300">
                            {item.productName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            🏪 {item.warehouseName}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center mb-1">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-2" />
                            <span className="text-sm font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded-full">
                              {item.quantity} / {item.minQuantity}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">{item.unit}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-sm">{t('dashboard.noLowStock')}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
    </div>
  );
};

export default DashboardModern;
